if (!customElements.get('newsletter-modal')) {
  customElements.define('newsletter-modal', class NewsletterModal extends HTMLElement {
    constructor() {
      super();
      // Trigger-only behaviour. The modal is opened exclusively by clicks
      // on links with href="#modal-newsletter" (handled globally by
      // Woolman.ModalsAndDrawers in global.js), OR on page load if the URL
      // indicates a successful newsletter submission from this modal.
    }

    connectedCallback() {
      const clearHashIfOurs = () => {
        if (window.location.hash === '#' + this.id) {
          try {
            history.replaceState(null, '', window.location.pathname + window.location.search);
          } catch (e) {
            window.location.hash = '';
          }
        }
      };

      // Strip ?customer_posted=true and the modal form hash from the URL so
      // reloads don't re-trigger the "just subscribed" state.
      const cleanPostSubmitUrl = () => {
        try {
          const url = new URL(window.location.href);
          let changed = false;
          if (url.searchParams.has('customer_posted')) {
            url.searchParams.delete('customer_posted');
            changed = true;
          }
          if (url.hash && url.hash.indexOf('#newsletter-modal-') === 0) {
            url.hash = '';
            changed = true;
          }
          if (changed) {
            history.replaceState(null, '', url.pathname + url.search + url.hash);
          }
        } catch (e) {}
      };

      // Remove duplicate success banners rendered by OTHER {% form 'customer' %}
      // instances on the page (e.g. footer newsletter) - Shopify flags
      // form.posted_successfully? on every such form after any customer submit.
      const suppressForeignSuccessMessages = () => {
        const selectors = [
          '.newsletter-form__message--success',
          '.form__message--success'
        ];
        selectors.forEach((sel) => {
          document.querySelectorAll(sel).forEach((el) => {
            // Keep only success markup that is part of our dedicated modal
            // success component. Any generic newsletter/form success message
            // (including those inside the modal) is treated as foreign.
            if (el.closest('.nl-modal-form__success')) return;
            if (el.parentNode) el.parentNode.removeChild(el);
          });
        });
      };

      this.addEventListener('click', (event) => {
        const target = event.target.closest('[data-close], [data-overlay], [data-modal-close], [data-modal-overlay]');
        if (target && this.contains(target)) {
          setTimeout(clearHashIfOurs, 0);
        }
      });

      document.addEventListener('keyup', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) {
          setTimeout(clearHashIfOurs, 0);
        }
      });

      // Inject the styled success block into a modal form that did not get a
      // server-rendered success state (e.g. because Shopify's /challenge flow
      // stripped our return_to query and Shopify did not set
      // form.posted_successfully on this page load). Clones the hidden
      // <template data-nl-modal-success-template> rendered by the snippet so
      // the HTML matches the admin-customizable title/body.
      // Tag the modal's content column with a --success modifier whenever the
      // success block is present. CSS uses this to hide the heading/intro/legal
      // and center the success message.
      const syncSuccessStateClass = () => {
        const content = this.querySelector('.newsletter-modal__content');
        if (!content) return;
        const hasSuccess = !!this.querySelector('.nl-modal-form__success');
        content.classList.toggle('newsletter-modal__content--success', hasSuccess);
      };

      const injectSuccessFallback = () => {
        const form = this.querySelector('form.nl-modal-form');
        if (!form) return;
        // Already rendered server-side or by a previous pass.
        if (form.querySelector('.nl-modal-form__success')) {
          syncSuccessStateClass();
          return;
        }
        const wrapper = form.querySelector('.nl-modal-form__field-wrapper');
        if (!wrapper) return;

        const tpl = this.querySelector('template[data-nl-modal-success-template]');
        if (tpl && tpl.content) {
          wrapper.innerHTML = '';
          wrapper.appendChild(tpl.content.cloneNode(true));
          syncSuccessStateClass();
          return;
        }
      };

      // Keep the content--success class in sync when the AJAX path (in the
      // snippet's own inline script) swaps success HTML into the form.
      try {
        const observer = new MutationObserver(syncSuccessStateClass);
        observer.observe(this, { childList: true, subtree: true });
      } catch (_) {}
      // Initial sync covers server-rendered success on first paint.
      syncSuccessStateClass();

      // --- Post-submission reopen flow ---
      // Reopen the modal when the user just submitted this modal's newsletter
      // form. Signals we look at (any is sufficient):
      //   1. sessionStorage flag we set before a challenge-triggered full-page
      //      submit (survives the /challenge redirect that strips our query
      //      and sometimes the hash).
      //   2. ?customer_posted=true query from our return_to.
      //   3. #newsletter-modal-<id> hash from our return_to (Shopify's
      //      challenge redirect often preserves the hash but loses the query).
      //
      // Do NOT auto-open on Shopify's /challenge page itself - the modal
      // would cover the captcha the user has to solve.
      let pendingFromSession = null;
      try { pendingFromSession = sessionStorage.getItem('nl-modal-pending-submit'); } catch (_) {}
      const modalForm = this.querySelector('form.nl-modal-form');
      const modalFormId = modalForm ? modalForm.id : null;
      const hash = (window.location.hash || '').replace(/^#/, '');
      const params = new URLSearchParams(window.location.search || '');
      const onChallengePage = /(^|\/)challenge(\/|$|\?)/i.test(window.location.pathname);
      const pendingMatchesThisModal = !!pendingFromSession && (!modalFormId || pendingFromSession === modalFormId);
      const submittedFromModal =
        !onChallengePage && (
          pendingMatchesThisModal ||
          params.get('customer_posted') === 'true' ||
          hash.indexOf('newsletter-modal-') === 0
        );

      if (submittedFromModal) {
        const runPostSubmit = () => {
          suppressForeignSuccessMessages();
          // Inject the success state if Shopify's response did not already
          // render it server-side (safe — injectSuccessFallback no-ops when
          // the block is already present).
          injectSuccessFallback();
          if (window.Woolman && Woolman.ModalsAndDrawers) {
            Woolman.ModalsAndDrawers.showModalOrDrawer(this.id);
          }
          cleanPostSubmitUrl();
          try { sessionStorage.removeItem('nl-modal-pending-submit'); } catch (_) {}
          // Re-sweep after a tick in case other scripts (e.g. footer) inject
          // their success banners during/after load.
          setTimeout(suppressForeignSuccessMessages, 50);
          setTimeout(suppressForeignSuccessMessages, 500);
        };
        if (document.readyState === 'complete') {
          setTimeout(runPostSubmit, 0);
        } else {
          window.addEventListener('load', runPostSubmit, { once: true });
        }
      } else if (window.location.hash === '#' + this.id) {
        // Legacy: clear a stale #modal-newsletter hash so the modal doesn't
        // reopen automatically on every reload.
        clearHashIfOurs();
      }

      // Honour test_mode so the theme editor can preview the open state.
      if (this.dataset.testMode === 'true' && window.Woolman && Woolman.ModalsAndDrawers) {
        Woolman.ModalsAndDrawers.showModalOrDrawer(this.id);
      }
    }
  });
}